import * as R from 'ramda'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import loadable from '@loadable/component'
import MenuItem from '@mui/material/MenuItem'
import React, {useState} from 'react'
import Select from '@mui/material/Select'

import {dynamicRouting, removeDuplicate} from 'helpers/utils'
import {footerPropTypes} from 'helpers/propTypes'
import Description from 'components/UI/Description'
import Media from 'components/UI/Media'
import useIsMobile from 'hooks/useIsMobile'

import useStyles from './styles'

const FooterRoot = ({pageContext, countryData, data}) => {
  const CollapsableMenu = loadable(() =>
    import('components/Layout/CollapsableMenu'),
  )
  const RoundButton = loadable(() => import('components/UI/RoundButton'))
  const Social = loadable(() => import('components/UI/Social'))
  const UiLink = loadable(() => import('components/UI/Link'))
  const {nodeLocale, prefix, hasShortPath} = pageContext
  const {countryCode, navLocale, technicalName, urlPrefix} = countryData

  const classes = useStyles()
  const mapIndexed = R.addIndex(R.map)
  const isMobile = useIsMobile()

  const [currentLocation, setCurrentLocation] = useState(0)

  const handleChange = e => {
    const {value} = e.target

    setCurrentLocation(value)
  }

  const localizedFooter = data.allContentfulFooter.edges.filter(
    el => el.node.node_locale === nodeLocale,
  )

  const allContentfulLocations = R.filter(
    el => el.node.node_locale === nodeLocale,
    R.pathOr(null, ['allContentfulLocations', 'edges'], data),
  )

  const allContentfulLocationsNodes = R.pathOr(
    null,
    [0, 'node', 'locations'],
    allContentfulLocations,
  )

  const mappedCountries = R.map(
    el => el.country && R.map(e => e.urlPrefix === urlPrefix && el, el.country),
    allContentfulLocationsNodes,
  )

  const countryLocations = removeDuplicate(
    R.pipe(
      R.flatten,
      R.filter(el => el !== false),
      R.filter(el => el !== null),
    )(mappedCountries),
  )

  const {
    logo,
    socialMediaLinks,
    locations,
    firstFooterMenu,
    secondFooterMenu,
    thirdFooterMenu,
    bottomMenu,
  } = R.pathOr(null, [0, 'node'], localizedFooter)
  const menuItemsLists = [firstFooterMenu, secondFooterMenu, thirdFooterMenu]

  return (
    <footer className={`${classes.container} sideWrapper`} id="page-footer">
      <div className={classes.menu}>
        <div className={classes.top}>
          <div className={classes.leftSide}>
            <div className={classes.leftSideTop}>
              <div className={classes.logoKeyrus}>
                <Media data={logo} />
              </div>
              <div className={classes.formControl}>
                <Select
                  MenuProps={{disableScrollLock: true}}
                  value={currentLocation}
                  onChange={handleChange}
                  variant="standard"
                  disableUnderline
                  IconComponent={() => (
                    <ArrowDownIcon className={classes.selectIcon} />
                  )}
                  classes={{
                    outlined: classes.toto,
                    root: classes.outlinedInput,
                    focused: classes.outlinedInputFocused,
                  }}
                >
                  {mapIndexed((node, index) => {
                    const {id, name} = node

                    return (
                      <MenuItem key={`${id}---${index}`} value={index}>
                        <div>{name}</div>
                      </MenuItem>
                    )
                  }, countryLocations)}
                </Select>
              </div>
              <div className={classes.infos}>
                <Description>
                  {R.pathOr(
                    '',
                    ['address', 'address'],
                    countryLocations[currentLocation],
                  )}
                </Description>
                {R.pathOr(
                  null,
                  ['phone'],
                  countryLocations[currentLocation],
                ) && (
                  <Description>
                    {locations.phoneLabel}
                    {countryLocations[currentLocation].phone}
                  </Description>
                )}
                {R.pathOr(null, ['fax'], countryLocations[currentLocation]) && (
                  <Description>
                    {locations.faxLabel}
                    {countryLocations[currentLocation].fax}
                  </Description>
                )}
              </div>
            </div>
            <div className={classes.socialIcons}>
              <Social
                socialMediaLinks={socialMediaLinks}
                hasMarginTop={isMobile}
              />
            </div>
          </div>
          <nav className={classes.rightSide}>
            {mapIndexed((node, index) => {
              const isOtherMenu = R.equals(node.name, 'Others')
              const contactUsLink =
                isOtherMenu &&
                (R.equals(countryCode, 'worldwide')
                  ? `/${countryCode}/${node.menuItems[0].link}`
                  : `/${countryCode}/${navLocale}/${node.menuItems[0].link}`)

              return (
                <>
                  {!isOtherMenu ? (
                    <CollapsableMenu
                      node={node}
                      id={`node.id${index}`}
                      prefix={prefix}
                      country={technicalName}
                      countryCode={countryCode}
                      hasShortPath={hasShortPath}
                      navLocale={navLocale}
                    />
                  ) : (
                    <div className={classes.contactUsWrapper}>
                      <RoundButton
                        color="primary"
                        centered
                        isSmallText
                        href={contactUsLink}
                        isNotBold
                      >
                        {node.menuItems[0].name}
                      </RoundButton>
                    </div>
                  )}
                </>
              )
            }, menuItemsLists)}
          </nav>
        </div>

        <div className={classes.bottomSide}>
          {R.map(
            ({
              link,
              id,
              title,
              isExternalLink,
              isOpenInNewWindow,
              isSameMenuItemForAllCountries,
              menuItemPerCountry,
            }) => (
              <div className={`${classes.navigationItemGrey} ${classes.legal}`}>
                <UiLink
                  path={
                    link === 'data-protection'
                      ? dynamicRouting(
                          prefix,
                          `about-us/${link}`,
                          countryCode,
                          hasShortPath,
                          navLocale,
                        )
                      : dynamicRouting(
                          prefix,
                          link,
                          countryCode,
                          hasShortPath,
                          navLocale,
                        )
                  }
                  key={id}
                  label={title}
                  isExternalLink={isExternalLink}
                  isOpenInNewWindow={isOpenInNewWindow}
                  isSameMenuItemForAllCountries={isSameMenuItemForAllCountries}
                  menuItemPerCountry={menuItemPerCountry}
                  country={technicalName}
                />
              </div>
            ),
            bottomMenu.menuItems,
          )}
        </div>
      </div>
    </footer>
  )
}

FooterRoot.propTypes = footerPropTypes.isRequired

export default FooterRoot
